import DataGrid, {
  Column,
  ColumnFixing,
  FilterRow,
  Grouping,
  GroupPanel,
  HeaderFilter,
  IColumnProps,
  Item,
  Paging,
  Scrolling,
  Selection,
  Sorting,
  Toolbar
} from "devextreme-react/data-grid";
import "devextreme/dist/css/dx.light.css";
import React from "react";
import "./dataGridView.css";

import { LinearProgress } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import CustomStore from "devextreme/data/custom_store";

import { Properties } from "devextreme/ui/data_grid";
import { RenderableColumnDefinition } from "./gridTypings";

const useStyles = makeStyles(theme => ({
  gridRoot: { overflow: "hidden", height: "100%" },
  item: {
    minHeight: 50
  }
}));

type DxGridProps = Omit<Properties, "columns">;

export interface GridProps<
  TRowData,
  TColDef extends Omit<IColumnProps, "cellRenderer">
> extends DxGridProps {
  /** Rows with data objects (e.g `[{col1: "value1", col2: 7}]`). Used to infer cell renderer input typings. */
  rowData?: TRowData[];
  /** Column definitions each consisting of:
   *
   *    @param colDef a devextreme-react/data-grid column definition without `cellRender`
   *    @param renderer a typed version of `cellRender`
   */
  columns?: RenderableColumnDefinition<TColDef, TRowData>[];
  isLoading?: boolean;
  withToolbar?: boolean;
  toolbarItemRender?: () => React.ReactNode;
  toolbarItemLocation?: string;
  allowGrouping?: boolean;
  load?: any;
  allowSelection?: boolean;
  children?: React.ReactNode;
  showFilterRow?: boolean;
}

export const TestIds = {
  loadingIndicator: "loadingIndicator",
  gridContent: "dataGrid"
};

const DataGridView = <TRowData extends {}>({
  rowData,
  columns,
  isLoading,
  withToolbar,
  toolbarItemRender,
  toolbarItemLocation = "after",
  allowGrouping = false,
  showFilterRow = true,
  load,
  allowSelection = true,
  children,
  ...dataGridProps
}: GridProps<TRowData, IColumnProps>) => {
  const classes = useStyles();

  const gridProps = {
    cellHintEnabled: true,
    allowColumnReordering: true,
    allowColumnResizing: true,
    showBorders: true,
    height: "100%",
    width: "100%",
    columnAutoWidth: true,
    remoteOperations: false,
    ...dataGridProps
  };

  const { height, remoteOperations } = gridProps;

  return (
    <div className={classes.gridRoot}>
      {isLoading && <LinearProgress data-testid={TestIds.loadingIndicator} />}
      <div
        data-testid={TestIds.gridContent}
        style={{ height: typeof height == "function" ? height() : height }}
      >
        <DataGrid
          dataSource={
            !remoteOperations
              ? rowData
              : new CustomStore({
                  load
                })
          }
          {...gridProps}
        >
          <Scrolling
            mode={remoteOperations ? "virtual" : "standard"}
            columnRenderingMode="standard"
          />
          <Paging enabled={remoteOperations} />
          <GroupPanel visible={allowGrouping} />
          {allowGrouping && <Grouping autoExpandAll={true} />}
          {allowSelection && <Selection mode="single" />}
          <Sorting mode="multiple" />
          <FilterRow visible={showFilterRow} />
          <HeaderFilter visible={true} />
          <ColumnFixing enabled={true} />
          {columns?.map(({ colDef, render }, index) => {
            return (
              <Column
                {...colDef}
                cellRender={render}
                alignment="left"
                key={index}
              />
            );
          })}
          {withToolbar && toolbarItemRender ? (
            <Toolbar>
              <Item
                location={toolbarItemLocation}
                render={toolbarItemRender}
                cssClass={classes.item}
              />
            </Toolbar>
          ) : null}
          {children}
        </DataGrid>
      </div>
    </div>
  );
};
export default DataGridView;
